// Per-map reverb impulse responses (§13.1): stereo, exponentially decaying filtered noise. Pure:
// returns sample arrays that the mixer copies into an AudioBuffer for its ConvolverNode.
import { MapId } from '@shared/ids';
import { mulberry32 } from './util';

export interface ReverbSpec {
  /** Decay to −60 dB, seconds. */
  rt60: number;
  /** One-pole low-pass on the tail at the start and at the end (the tail darkens as it decays). */
  brightHz: number;
  darkHz: number;
  /** Band-pass centre (metallic rooms), or 0. */
  bandHz: number;
  /** Discrete early reflections: [delay s, gain] (a slap-back gives a small, hard room). */
  early: readonly (readonly [number, number])[];
  /** Silence before the diffuse tail, seconds. */
  predelay: number;
}

export const REVERBS: Readonly<Record<MapId, ReverbSpec>> = {
  [MapId.LOBBY]: { rt60: 0.8, brightHz: 5200, darkHz: 1800, bandHz: 0, early: [[0.011, 0.35], [0.019, 0.25]], predelay: 0.008 },
  [MapId.PODIUM]: { rt60: 0.8, brightHz: 5200, darkHz: 1800, bandHz: 0, early: [[0.011, 0.35], [0.019, 0.25]], predelay: 0.008 },
  // Bright slap: a hard porcelain surface, two strong early taps and a short bright tail.
  [MapId.TEACUP]: { rt60: 0.6, brightHz: 9000, darkHz: 4200, bandHz: 0, early: [[0.007, 0.55], [0.023, 0.42], [0.037, 0.2]], predelay: 0.004 },
  // Hall: long and wide.
  [MapId.PINBALL]: { rt60: 2.2, brightHz: 6500, darkHz: 1500, bandHz: 0, early: [[0.017, 0.3], [0.031, 0.25], [0.047, 0.18]], predelay: 0.02 },
  // Soft: frosting and sponge swallow the highs.
  [MapId.CAKE]: { rt60: 1.0, brightHz: 3200, darkHz: 1100, bandHz: 0, early: [[0.013, 0.2]], predelay: 0.01 },
  // Metallic drum: band-passed at 1.8 kHz with a ringing comb.
  [MapId.SPIN]: { rt60: 1.4, brightHz: 7000, darkHz: 2600, bandHz: 1800, early: [[0.009, 0.4], [0.018, 0.3], [0.027, 0.22]], predelay: 0.006 },
};

/** Stereo IR for `spec` at `sampleRate`; unit energy per channel, so maps differ in colour, not level. */
export function generateIR(spec: ReverbSpec, sampleRate: number, seed = 1): [Float32Array<ArrayBuffer>, Float32Array<ArrayBuffer>] {
  const len = Math.max(1, Math.round(sampleRate * (spec.predelay + spec.rt60 * 1.05)));
  const out: [Float32Array<ArrayBuffer>, Float32Array<ArrayBuffer>] = [new Float32Array(len), new Float32Array(len)];
  const pre = Math.round(spec.predelay * sampleRate);
  const decayPerSample = Math.log(1000) / (spec.rt60 * sampleRate);
  // Built while the game runs (after unlock): the darkening low-pass is updated every 32 samples
  // and the decay is a running product, so no transcendental runs per sample.
  const decayStep = Math.exp(-decayPerSample);
  const aHi = 1 - Math.exp((-2 * Math.PI * spec.bandHz * 0.6) / sampleRate);
  const aLo = 1 - Math.exp((-2 * Math.PI * spec.bandHz * 1.6) / sampleRate);
  const span = Math.max(1, len - pre);
  for (let ch = 0; ch < 2; ch++) {
    const d = out[ch];
    const r = mulberry32(seed * 977 + ch * 131 + 7);
    let lp = 0;
    // Band-pass state (two one-pole sections around bandHz) for the metallic room.
    let bpLo = 0;
    let bpHi = 0;
    const comb = spec.bandHz > 0 ? Math.round(sampleRate / spec.bandHz) * 3 : 0;
    let a = 0;
    let env = 1;
    for (let i = pre; i < len; i++) {
      if ((i - pre) % 32 === 0) {
        const cutoff = spec.brightHz * (spec.darkHz / spec.brightHz) ** ((i - pre) / span);
        a = 1 - Math.exp((-2 * Math.PI * cutoff) / sampleRate);
      }
      const w = r() * 2 - 1;
      lp += a * (w - lp);
      let v = lp;
      if (spec.bandHz > 0) {
        bpLo += aLo * (v - bpLo);
        bpHi += aHi * (bpLo - bpHi);
        v = 0.45 * v + 1.6 * (bpLo - bpHi);
        if (comb > 0 && i - comb >= pre) v += 0.35 * d[i - comb];
      }
      d[i] = v * env;
      env *= decayStep;
    }
    for (const [delay, gain] of spec.early) {
      const i = Math.round(delay * sampleRate * (ch === 0 ? 1 : 1.13));
      if (i < len) {
        d[i] += gain * (ch === 0 ? 1 : 0.9);
        if (i + 1 < len) d[i + 1] -= gain * 0.3;
      }
    }
    // Fade the very start (no click at the convolution onset) and the end.
    const fadeIn = Math.min(len, Math.round(sampleRate * 0.002));
    for (let i = 0; i < fadeIn && pre + i < len; i++) d[pre + i] *= i / fadeIn;
    const fadeOut = Math.min(len, Math.round(sampleRate * 0.05));
    for (let i = 0; i < fadeOut; i++) d[len - 1 - i] *= i / fadeOut;
    let e = 0;
    for (let i = 0; i < len; i++) e += d[i] * d[i];
    const norm = e > 0 ? 1 / Math.sqrt(e) : 0;
    for (let i = 0; i < len; i++) d[i] *= norm;
  }
  return out;
}
